import { cloneDeep, get, map } from 'lodash';

import ChargerNodeIcon from '../../resources/Charger_Node.svg';
import CombinedResourceIcon from '../../resources/Combined_source.svg';
import ConstraintNodeIcon from '../../resources/Constraint_Node.svg';
import JunctionNodeIcon from '../../resources/Junction.svg';
import LoadNodeIcon from '../../resources/Load_Node.svg';
import MeterIcon from '../../resources/Meter_Node.svg';
import ChargingPolicyNodeIcon from '../../resources/Policy_Node.svg';
import RenewableIcon from '../../resources/Renewable_energy.svg';
import RootNodeIcon from '../../resources/Root_Node.svg';
import StorageIcon from '../../resources/Storage.svg';
import { detectDemoMode } from '../Demo/helper';
import { CONSTRAINT_CALCULATE_VALUE_RULES } from '../Editor/NodeForm/rules/constraint';
import { measurementTypes } from './constants';
import { isMobile } from '../../../../common/services/isMobile';

const isDemo = detectDemoMode();

export const MONITORING_VIEW_TREE = 'tree';
export const MONITORING_VIEW_TABLE = 'table';

// SCHEMA

export const VIEW_SCHEMA_MODE = 'view';
export const EDIT_SCHEMA_MODE = 'edit';

export const SCHEMA_TOP_INDENT = isMobile ? 165 : 110;
export const SCHEMA_LEFT_INDENT = isMobile ? 288 : 350;

// SCHEDULE CONFIG

export const PGE_SCHEDULE_TYPE = 'PGE';
export const ADDITIONAL_POWER_SCHEDULE_TYPE = 'ADDITIONAL_POWER';
export const LOAD_POWER_SCHEDULE_TYPE = 'LOAD';
export const CHARGING_POLICY_SCHEDULE_TYPE = 'CHARGING_POLICY';
export const POWER_MAX_LIMIT_SCHEDULE_TYPE = 'POWER_MAX_LIMIT';
export const LOAD_MAX_LIMIT_SCHEDULE_TYPE = 'LOAD_MAX_LIMIT';

export const CHARGING_ENABLED_CHARGING_POLICY = 'CHARGING_ENABLED';
export const DISCHARGING_ENABLED_CHARGING_POLICY = 'DISCHARGING_ENABLED';
export const CHARGING_DISCHARGING_ENABLED_CHARGING_POLICY = 'CHARGING_DISCHARGING_ENABLED';
export const CHARGING_DISCHARGING_DISABLED_CHARGING_POLICY = 'CHARGING_DISCHARGING_DISABLED';

export const chargingPolicyMap = {
  [CHARGING_ENABLED_CHARGING_POLICY]: {
    label: 'Charging enabled',
    isActive: true,
  },
  [DISCHARGING_ENABLED_CHARGING_POLICY]: {
    label: 'Discharging enabled',
    isActive: true,
  },
  [CHARGING_DISCHARGING_ENABLED_CHARGING_POLICY]: {
    label: 'Charging / Discharging enabled',
    isActive: false,
  },
  [CHARGING_DISCHARGING_DISABLED_CHARGING_POLICY]: {
    label: 'Charging / Discharging disabled',
    isActive: false,
  },
};

export const makeChargingPolicyInputOptions = () => map(chargingPolicyMap, (policy, value) => ({ value, label: policy.label, disabled: !policy.isActive }));

export const OFF_PEAK_PGE_PERIOD = 'OFF_PEAK';
export const PARTIAL_PEAK_PGE_PERIOD = 'PARTIAL_PEAK';
export const PEAK_PGE_PERIOD = 'PEAK';

export const PGEPeriodLabelMap = {
  [OFF_PEAK_PGE_PERIOD]: 'Off-Peak',
  [PARTIAL_PEAK_PGE_PERIOD]: 'Partial-Peak',
  [PEAK_PGE_PERIOD]: 'Peak',
};

export const ROOT_NODE_TYPE = 'ROOT';
export const CHARGER_NODE_TYPE = 'CHARGER';
export const CONSTRAINT_NODE_TYPE = 'CONSTRAINT';
export const POWER_SOFT_CONSTRAINT_NODE_TYPE = 'POWER_SOFT_CONSTRAINT';
export const LOAD_SOFT_CONSTRAINT_NODE_TYPE = 'LOAD_SOFT_CONSTRAINT';
export const DYNAMIC_SOFT_CONSTRAINT_NODE_TYPE = 'DYNAMIC_SOFT_CONSTRAINT';
export const MEASUREMENT_POINT_NODE_TYPE = 'MEASUREMENT_POINT';
export const LOAD_NODE_TYPE = 'LOAD';
export const JUNCTION_NODE_TYPE = 'JUNCTION';
export const RENEWABLE_NODE_TYPE = 'RENEWABLE';
export const STORAGE_NODE_TYPE = 'STORAGE';
export const RENEWABLE_WITH_STORAGE_NODE_TYPE = 'RENEWABLE_WITH_STORAGE';
export const CHARGING_POLICY_NODE_TYPE = 'CHARGING_POLICY';
export const undefinedNodeParamValue = 'N/A';

export const RESOURCE_NODE_TYPES = [
  RENEWABLE_WITH_STORAGE_NODE_TYPE,
  RENEWABLE_NODE_TYPE,
  STORAGE_NODE_TYPE,
];

export const CONSTRAINT_NODE_TYPES = [
  LOAD_SOFT_CONSTRAINT_NODE_TYPE,
  POWER_SOFT_CONSTRAINT_NODE_TYPE,
  DYNAMIC_SOFT_CONSTRAINT_NODE_TYPE,
  CONSTRAINT_NODE_TYPE,
];

export const STATE_TYPE_EV_CONNECTED = 'EV_CONNECTED';
export const STATE_TYPE_CHARGING = 'CHARGING';
export const STATE_TYPE_CHARGING_WITH_ERROR = 'CHARGING_WITH_ERROR';
export const STATE_TYPE_DISCHARGING = 'DISCHARGING';
export const STATE_TYPE_DISCHARGING_WITH_ERROR = 'DISCHARGING_WITH_ERROR';
export const STATE_TYPE_OFFLINE = 'OFFLINE';
export const STATE_TYPE_AUTHORIZATION_REQUEST = 'AUTHORIZATION_REQUEST';
export const STATE_TYPE_APPLYING_LIMIT = 'APPLYING_LIMIT';
export const STATE_TYPE_CLASS_NAMES = {
  [STATE_TYPE_EV_CONNECTED]: 'dynamic-icon evConnected active preload',
  [STATE_TYPE_CHARGING]: 'dynamic-icon charging active preload',
  [STATE_TYPE_CHARGING_WITH_ERROR]: 'dynamic-icon chargingError active preload',
  [STATE_TYPE_DISCHARGING]: 'dynamic-icon discharging active preload',
  [STATE_TYPE_DISCHARGING_WITH_ERROR]: 'dynamic-icon dischargingError active preload',
  [STATE_TYPE_OFFLINE]: 'dynamic-icon online active preload',
  [STATE_TYPE_AUTHORIZATION_REQUEST]: 'dynamic-icon authorizationRequested active preload',
  [STATE_TYPE_APPLYING_LIMIT]: 'dynamic-icon applyingLimit active preload',
};

export const NODES_WITHOUT_SOCKET_IN = [
  CHARGER_NODE_TYPE,
  RENEWABLE_NODE_TYPE,
  RENEWABLE_WITH_STORAGE_NODE_TYPE,
  STORAGE_NODE_TYPE,
  LOAD_NODE_TYPE,
];

export const defaultSchedule = {
  [ROOT_NODE_TYPE]: {
    type: PGE_SCHEDULE_TYPE,
    from: 0,
    to: 86399,
    cost: 0.0,
    maxPeakDemand: 0,
    pgePeriod: OFF_PEAK_PGE_PERIOD,
  },
  [RENEWABLE_NODE_TYPE]: {
    type: ADDITIONAL_POWER_SCHEDULE_TYPE,
    from: 0,
    to: 86399,
    expectedPower: 0.0,
  },
  [LOAD_NODE_TYPE]: {
    type: LOAD_POWER_SCHEDULE_TYPE,
    from: 0,
    to: 86399,
    minLoad: 0.0,
    peakLoad: 0.0,
    minPower: 0.0,
    peekPower: 0.0,
  },
  [CHARGING_POLICY_NODE_TYPE]: {
    type: CHARGING_POLICY_SCHEDULE_TYPE,
    from: 0,
    to: 86399,
    chargingPolicy: CHARGING_ENABLED_CHARGING_POLICY,
  },
  [POWER_SOFT_CONSTRAINT_NODE_TYPE]: {
    type: POWER_MAX_LIMIT_SCHEDULE_TYPE,
    from: 0,
    to: 86399,
    maxPower: 0,
  },
  [LOAD_SOFT_CONSTRAINT_NODE_TYPE]: {
    type: LOAD_MAX_LIMIT_SCHEDULE_TYPE,
    from: 0,
    to: 86399,
    maxLoad: 0,
  },
};

export const WEEKDAYS_SCHEDULE_DAY_TYPE = 'WEEKDAYS';
export const WEEKENDS_SCHEDULE_DAY_TYPE = 'WEEKENDS';
export const ALL_DAYS_SCHEDULE_DAY_TYPE = 'ALL';

export const makeDefaultSchedule = (nodeType, doubleSet = true) => (
  doubleSet
    ? [
      {
        dayType: WEEKDAYS_SCHEDULE_DAY_TYPE,
        items: [{ ...defaultSchedule[nodeType] }],
      },
      {
        dayType: WEEKENDS_SCHEDULE_DAY_TYPE,
        items: [{ ...defaultSchedule[nodeType] }],
      },
    ]
    : [
      {
        dayType: ALL_DAYS_SCHEDULE_DAY_TYPE,
        items: [{ ...defaultSchedule[nodeType] }],
      },
    ]
);

// DEFAULT NODE PROPS

export const defaultPhases = {
  relativeRotation: { 1: '1', 2: '2', 3: '3' },
};

export const defaultNodeStaticProps = {
  [ROOT_NODE_TYPE]: ({ plazaSettings = {}, ...props }) => {
    let safetyMarginPercent = null;

    if (plazaSettings) {
      safetyMarginPercent = plazaSettings.defaultSafetyMarginPercent || 5;
    }

    return {
      nodeName: 'Grid',
      maxCurrentLimit: 0,
      voltage: 0,
      safetyMarginPercent,
      schedules: makeDefaultSchedule(ROOT_NODE_TYPE),
      ...props,
    };
  },
  [CHARGER_NODE_TYPE]: ({ plazaSettings = {}, ...props }) => {
    let minLoadLimit = null;
    let offlineCurrentLimit = null;

    if (plazaSettings) {
      minLoadLimit = get(plazaSettings, 'defaultMinLoadLimit', 6);
      offlineCurrentLimit = get(plazaSettings, 'defaultOfflineLoadLimit', 6);
    }

    return {
      nodeName: 'Charger',
      chargerUuid: null,
      phases: cloneDeep(defaultPhases),
      minLoadLimit,
      offlineCurrentLimit,
      ...props,
    };
  },
  [CONSTRAINT_NODE_TYPE]: (props = {}) => ({
    nodeName: 'Hard constraint',
    maxCurrentLimit: '',
    phases: cloneDeep(defaultPhases),
    ...props,
  }),
  [LOAD_NODE_TYPE]: (props = {}) => ({
    nodeName: 'Load',
    offlineCurrentLimit: '',
    phasesConnectivityType: 3,
    phases: cloneDeep(defaultPhases),
    schedules: makeDefaultSchedule(LOAD_NODE_TYPE),
    ...props,
  }),
  [POWER_SOFT_CONSTRAINT_NODE_TYPE]: (props = {}) => ({
    nodeName: 'Power Soft constraint',
    maxPower: '',
    phases: cloneDeep(defaultPhases),
    schedules: makeDefaultSchedule(POWER_SOFT_CONSTRAINT_NODE_TYPE),
    ...props,
  }),
  [LOAD_SOFT_CONSTRAINT_NODE_TYPE]: (props = {}) => ({
    nodeName: 'Load Soft constraint',
    maxCurrentLimit: 0,
    maxLoad: '',
    phases: cloneDeep(defaultPhases),
    schedules: makeDefaultSchedule(LOAD_SOFT_CONSTRAINT_NODE_TYPE),
    ...props,
  }),
  [MEASUREMENT_POINT_NODE_TYPE]: (props = {}) => ({
    nodeName: 'Measurement Point',
    increaseThreshold: 5,
    decreaseThreshold: 5,
    phases: cloneDeep(defaultPhases),
    measurementNodeType: measurementTypes.LOAD,
    ...props,
  }),
  [JUNCTION_NODE_TYPE]: (props = {}) => ({
    nodeName: 'Junction',
    ...props,
  }),
  [CHARGING_POLICY_NODE_TYPE]: (props = {}) => ({
    nodeName: 'Charging Policy',
    chargingPolicy: CHARGING_ENABLED_CHARGING_POLICY,
    schedules: makeDefaultSchedule(CHARGING_POLICY_NODE_TYPE),
    ...props,
  }),
  [RENEWABLE_NODE_TYPE]: (props = {}) => ({
    nodeName: 'Renewable',
    maxCurrentLimit: '',
    voltage: '',
    withMeasurementPoint: true,
    withHardConstraint: true,
    junctionNodeId: null,
    schedules: makeDefaultSchedule(RENEWABLE_NODE_TYPE, false),
    ...props,
  }),
  [RENEWABLE_WITH_STORAGE_NODE_TYPE]: (props = {}) => ({
    nodeName: 'Renewable with Storage',
    maxCurrentLimit: '',
    voltage: '',
    maxStorageCapacity: '',
    withMeasurementPoint: true,
    withHardConstraint: true,
    junctionNodeId: null,
    ...props,
  }),
  [STORAGE_NODE_TYPE]: (props = {}) => ({
    nodeName: 'Storage',
    maxCurrentLimit: '',
    voltage: '',
    maxStorageCapacity: '',
    withMeasurementPoint: true,
    withHardConstraint: true,
    sourceOrdinalNumber: 1,
    junctionNodeId: null,
    ...props,
  }),
  [DYNAMIC_SOFT_CONSTRAINT_NODE_TYPE]: (props = {}) => ({
    nodeName: 'Dynamic Soft Constraint Node',
    ...props,
  }),
};

export const INVALID_RULES_FORMAT_ERROR_MESSAGE = 'Invalid rules format';
export const INVALID_DATA_FORMAT_ERROR_MESSAGE = 'Invalid data format';

export const RootNodeConfig = () => ({
  color: '#a1457e',
  bg: '#f2e5ec',
  icon: RootNodeIcon,
  height: 207,
});

export const ChargerNodeConfig = (props = {}) => ({
  color: props.online ? '#72bcc4' : '#d0cece',
  icon: ChargerNodeIcon,
  height: props.condition === 'INACTIVE' ? (isDemo === 'v2x' ? 418 : 400) : (isDemo === 'v2x' ? 388 : 370),
});

export const ChargingPolicyNodeConfig = () => ({
  color: '#a1a0ea',
  bg: '#f2f1fc',
  icon: ChargingPolicyNodeIcon,
  staticParams: [],
  dynamicParams: [
    { label: 'Policy', name: 'chargingPolicy' },
  ],
});

export const ConstrainsNodeConfig = () => ({
  color: '#728fc3',
  bg: '#ebeff6',
  icon: ConstraintNodeIcon,
  height: 171,
});

export const JunctionNodeConfig = () => ({
  color: '#67224e',
  bg: '#e9dfe5',
  icon: JunctionNodeIcon,
  height: 40,
  width: 210,
});

export const LoadNodeConfig = () => ({
  color: '#8EA6B2',
  bg: '#eff2f4',
  icon: LoadNodeIcon,
  height: 58,
  staticParams: [],
  dynamicParams: [],
});

export const PARAMS_LABEL = {
  presentLoad: 'Present Load',
  presentLoadPhA: 'Present Load Ph-1',
  presentLoadPhB: 'Present Load Ph-2',
  presentLoadPhC: 'Present Load Ph-3',
  presentPower: 'Present Power',
  presentPowerPhA: 'Present Power Ph-1',
  presentPowerPhB: 'Present Power Ph-2',
  presentPowerPhC: 'Present Power Ph-3',
  presentVoltagePhA: 'Present Voltage Ph-1',
  presentVoltagePhB: 'Present Voltage Ph-2',
  presentVoltagePhC: 'Present Voltage Ph-3',
  sessionEnergy: 'Session Energy',
};

export const PARAMS_LABEL_FOR_DEMO = {
  ...PARAMS_LABEL,
  presentLoadPhA: 'Plaza Load Ph-1',
  presentLoadPhB: 'Plaza Load Ph-2',
  presentLoadPhC: 'Plaza Load Ph-3',
  presentPowerPhA: 'Plaza Power Ph-1',
  presentPowerPhB: 'Plaza Power Ph-2',
  presentPowerPhC: 'Plaza Power Ph-3',
}

export const MeasurementPointNodeConfig = () => ({
  color: '#76709E',
  bg: '#ebeaf1',
  icon: MeterIcon,
  staticParams: [],
  dynamicParams: [
    {
      label: PARAMS_LABEL.presentPower,
      name: 'presentPower',
      unit: 'kW',
      multiply: 0.001,
    },
  ],
  height: 153,
});

export const RenewableNodeConfig = () => ({
  color: '#9ec771',
  bg: '#f1f7ea',
  icon: RenewableIcon,
  height: 128,
});

export const RenewableStorageNodeConfig = () => ({
  color: '#589b78',
  bg: '#f1f7ea',
  icon: CombinedResourceIcon,
  height: 165,
});

export const PowerSoftConstraintNodeConfig = () => ({
  color: '#fdc461',
  bg: '#fff7e9',
  icon: ConstraintNodeIcon,
  staticParams: [
    {
      label: 'Max Power', name: 'maxPower', unit: 'kW', multiply: 0.001,
    },
  ],
  height: 171,
});

export const LoadSoftConstraintNodeConfig = () => ({
  color: '#fdc461',
  bg: '#fff7e9',
  icon: ConstraintNodeIcon,
  height: 171,
});

export const StorageNodeConfig = () => ({
  color: '#2f5944',
  bg: '#e1e7e4',
  icon: StorageIcon,
  height: 147,
});

export const NODES_DESCRIPTION = {
  [ROOT_NODE_TYPE]: {
    title: 'Root Node',
    description: 'Grid Node represents a Source Node, that is the main source of energy in the topology.',
  },
  [CHARGER_NODE_TYPE]: {
    title: 'Charger Node',
    description: 'Charger Node represents the node that consumes or provides energy.',
  },
  [CHARGING_POLICY_NODE_TYPE]: {
    title: 'Charging Policy Node',
    description: 'Charging Policy Node represents the node used to regulate the availability of charging and discharging in the topology for specific time slots.',
  },
  [JUNCTION_NODE_TYPE]: {
    title: 'Junction Node',
    description: 'Junction Node represents the connection point of different Source nodes and gives an opportunity to branch the topology.',
  },
  [CONSTRAINT_NODE_TYPE]: {
    title: 'Hard Constraint Node',
    description: 'Hard Constraint represents the breaker that limits maximum load on the branches of the topology. Each Breaker limits load only for its child Charger Nodes.',
  },
  [POWER_SOFT_CONSTRAINT_NODE_TYPE]: {
    title: 'Power Soft Constraint Node',
    description: 'Power Soft Constraint Node represents the limiter that limits maximum power of the topology or its branches. Each limiter limits power only for its child Charger Nodes.',
  },
  [LOAD_SOFT_CONSTRAINT_NODE_TYPE]: {
    title: 'Load Soft Constraint Node',
    description: 'Load Soft Constraint Node represents the limiter that limits maximum load of the topology or its branches. Each limiter limits load only for its child Charger Nodes.',
  },
  [LOAD_NODE_TYPE]: {
    title: 'Load Node',
    description: 'Load Node represents the uncontrollable load that consumes energy.',
  },
  [MEASUREMENT_POINT_NODE_TYPE]: {
    title: 'Measurement Point Node',
    description: 'Measurement Point represents the node that provides real-time values obtained from Meters. ',
  },
  [RENEWABLE_NODE_TYPE]: {
    title: 'Renewable Node',
    description: 'Renewable Source Node represents a Source Node, that is an additional source of renewable energy in the topology.',
  },
  [RENEWABLE_WITH_STORAGE_NODE_TYPE]: {
    title: 'Renewable with Storage',
    description: 'Renewable Source Node with storage represents a Source Node, that is an additional source and storage of renewable energy in the topology.',
  },
  [STORAGE_NODE_TYPE]: {
    title: 'Storage Node',
    description: 'Storage Source Node represents a Source Node, that is an additional storage of energy in the topology.',
  },
  [DYNAMIC_SOFT_CONSTRAINT_NODE_TYPE]: {
    title: 'Dynamic Soft Constraint Node',
    description: 'Dynamic Soft Constraint Node represents the limiter that limits maximum load of the topology or its branches. Each limiter limits load only for its child Charger Nodes.',
  },
};

export const NA = 'N/A ';

export const ZOOM_STEP = 10;
export const MAX_ZOOM_VALUE = 150;

export const CALCULATE_VALUE_RULES = {
  [CONSTRAINT_NODE_TYPE]: CONSTRAINT_CALCULATE_VALUE_RULES,
};

export const CURRENT_RMS_PHASE_A_MA = 'CURRENT_RMS_PHASE_A_MA';
export const CURRENT_RMS_PHASE_B_MA = 'CURRENT_RMS_PHASE_B_MA';
export const CURRENT_RMS_PHASE_C_MA = 'CURRENT_RMS_PHASE_C_MA';

export const POWER_ACTIVE_W = 'POWER_ACTIVE_W';
export const POWER_ACTIVE_PHASE_A_W = 'POWER_ACTIVE_PHASE_A_W';
export const POWER_ACTIVE_PHASE_B_W = 'POWER_ACTIVE_PHASE_B_W';
export const POWER_ACTIVE_PHASE_C_W = 'POWER_ACTIVE_PHASE_C_W';

export const VOLTAGE_RMS_PHASE_A_MV = 'VOLTAGE_RMS_PHASE_A_MV';
export const VOLTAGE_RMS_PHASE_B_MV = 'VOLTAGE_RMS_PHASE_B_MV';
export const VOLTAGE_RMS_PHASE_C_MV = 'VOLTAGE_RMS_PHASE_C_MV';

export const SESSION_ENERGY_TOTAL_WS = 'SESSION_ENERGY_TOTAL_WS';

export const PHASE_LABEL_FOR_CHARGERS = {
  presentLoad: 'Present Load',
  presentLoadPhA: 'Present Load Ph-1',
  presentLoadPhB: 'Present Load Ph-2',
  presentLoadPhC: 'Present Load Ph-3',
  presentPower: 'Present Power',
  presentPowerPhA: 'Present Power Ph-1',
  presentPowerPhB: 'Present Power Ph-2',
  presentPowerPhC: 'Present Power Ph-3',
  presentVoltagePhA: 'Present Voltage Ph-1',
  presentVoltagePhB: 'Present Voltage Ph-2',
  presentVoltagePhC: 'Present Voltage Ph-3',
};

export const multipleMeters = {
  SESSION_ENERGY_TOTAL_WS: 'SESSION_ENERGY_TOTAL_WS:EVPLCCOM-VIRTUAL-METER-1',
};

export const CHARGERS_LATEST_METRICS = {
  CURRENT_RMS_PHASE_A_MA,
  CURRENT_RMS_PHASE_B_MA,
  CURRENT_RMS_PHASE_C_MA,

  POWER_ACTIVE_PHASE_A_W,
  POWER_ACTIVE_PHASE_B_W,
  POWER_ACTIVE_PHASE_C_W,

  VOLTAGE_RMS_PHASE_A_MV,
  VOLTAGE_RMS_PHASE_B_MV,
  VOLTAGE_RMS_PHASE_C_MV,

  [multipleMeters.SESSION_ENERGY_TOTAL_WS]: SESSION_ENERGY_TOTAL_WS,
};

export const METER_LATEST_METRICS = [
  CURRENT_RMS_PHASE_A_MA,
  CURRENT_RMS_PHASE_B_MA,
  CURRENT_RMS_PHASE_C_MA,

  POWER_ACTIVE_PHASE_A_W,
  POWER_ACTIVE_PHASE_B_W,
  POWER_ACTIVE_PHASE_C_W,
];

export const PARAM_NAMES = {
  CURRENT_RMS_PHASE_A_MA: 'presentLoadPhA',
  CURRENT_RMS_PHASE_B_MA: 'presentLoadPhB',
  CURRENT_RMS_PHASE_C_MA: 'presentLoadPhC',

  POWER_ACTIVE_PHASE_A_W: 'presentPowerPhA',
  POWER_ACTIVE_PHASE_B_W: 'presentPowerPhB',
  POWER_ACTIVE_PHASE_C_W: 'presentPowerPhC',

  VOLTAGE_RMS_PHASE_A_MV: 'presentVoltagePhA',
  VOLTAGE_RMS_PHASE_B_MV: 'presentVoltagePhB',
  VOLTAGE_RMS_PHASE_C_MV: 'presentVoltagePhC',

  [multipleMeters.SESSION_ENERGY_TOTAL_WS]: 'sessionEnergy',
};

export const VIRTUAL_NODES = [
  CHARGING_POLICY_NODE_TYPE,
  JUNCTION_NODE_TYPE,
  LOAD_SOFT_CONSTRAINT_NODE_TYPE,
  POWER_SOFT_CONSTRAINT_NODE_TYPE,
  DYNAMIC_SOFT_CONSTRAINT_NODE_TYPE,
];
